import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { adminApi, messageFrom } from "@/lib/api";
import { mergeLead, needsRefresh } from "./leadBoardState";

/**
 * The follow-up board: what is on it, and every way a lead can be moved.
 *
 * Dragging is tracked here rather than in the columns so that a card and the
 * column it is being dropped on can never disagree about what is in flight.
 */
export function useLeadBoard(onAuthError) {
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [serviceFilter, setServiceFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [dragRef, setDragRef] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [outcomePrompt, setOutcomePrompt] = useState(null);
  const [compareLead, setCompareLead] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const params = {};
        if (serviceFilter !== "all") params.service = serviceFilter;
        if (search) params.q = search;
        setBoard(await adminApi.pipeline(params));
      } catch (error) {
        if (!onAuthError?.(error)) toast.error(messageFrom(error));
      } finally {
        setLoading(false);
      }
    },
    [serviceFilter, search, onAuthError],
  );

  useEffect(() => {
    load();
  }, [load]);

  const reasons = board?.reasons || {};

  const applyLead = (updated) => {
    if (needsRefresh(board, updated)) setTimeout(() => load(true), 250);
    setBoard((current) => mergeLead(current, updated));
    setActive((current) =>
      current && current.quote_ref === updated.quote_ref ? updated : current,
    );
  };

  /** Ask for a reason the moment a lead is closed - that is when it is known. */
  const promptForReason = (lead, stage) => {
    if (!lead.needs_outcome_reason || !(reasons[stage] || []).length) return;
    setOutcomePrompt({
      lead,
      stage,
      stageLabel: lead.status_label,
      reasons: reasons[stage],
    });
  };

  const moveTo = async (card, status) => {
    if (!card || card.status === status) return;
    try {
      const data = await adminApi.updateLead(card.quote_ref, { status });
      applyLead(data.lead);
      toast.success(`${card.full_name} moved to ${data.lead.status_label}.`);
      promptForReason(data.lead, status);
    } catch (error) {
      if (!onAuthError?.(error)) toast.error(messageFrom(error));
    }
  };

  const allCards = useMemo(
    () => (board?.columns || []).flatMap((column) => column.cards),
    [board],
  );

  const drag = {
    ref: dragRef,
    target: dropTarget,
    start: (event, dragged) => {
      setDragRef(dragged.quote_ref);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", dragged.quote_ref);
    },
    over: (event, columnKey) => {
      event.preventDefault();
      setDropTarget(columnKey);
    },
    leave: (columnKey) => setDropTarget((current) => (current === columnKey ? null : current)),
    drop: (event, columnKey) => {
      event.preventDefault();
      setDropTarget(null);
      const card = allCards.find((c) => c.quote_ref === dragRef);
      setDragRef(null);
      moveTo(card, columnKey);
    },
  };

  return {
    loading,
    board,
    stages: board?.stages || [],
    reasons,
    emailConfigured: Boolean(board?.email?.configured),
    summary: board?.summary || {},
    quietDays: board?.gone_quiet_days ?? 7,
    filtered: Boolean(search || serviceFilter !== "all"),
    allCards,
    query,
    setQuery,
    serviceFilter,
    setServiceFilter,
    active,
    setActive,
    outcomePrompt,
    setOutcomePrompt,
    compareLead,
    setCompareLead,
    drag,
    load,
    applyLead,
    moveTo,
  };
}
